import { useRef, useEffect, useState, useCallback } from "react";
import { X, Eye, Heart, MapPin, TrendingUp, Clock, ChevronLeft, ChevronRight, MessageCircle, Sparkles } from "lucide-react";
import { cn } from "@/lib/utils";
import { useIsMobile } from "@/hooks/use-mobile";
import { getWhatsAppLink } from "@/lib/whatsapp";
import { useGlobalVideoController } from "@/hooks/useGlobalVideoController";

export interface PortfolioVideo {
  id: string;
  title: string;
  description?: string | null;
  industry: string;
  location: string;
  video_url: string;
  thumbnail_url?: string | null;
  campaign_type?: string | null;
  views: number;
  likes: number;
  comments?: number;
  conversions?: number;
  revenue: number;
  roi_multiplier?: number;
  format_type?: string | null;
}

interface VideoPlayerModalProps {
  video: PortfolioVideo;
  videos: PortfolioVideo[];
  onClose: () => void;
  onNavigate: (video: PortfolioVideo) => void;
}

const formatNumber = (num: number) => {
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return num.toString();
};

const formatBadge = (type: string | null | undefined) => {
  if (!type) return null;
  const map: Record<string, { label: string; color: string }> = {
    short: { label: "13–15s", color: "from-neon-cyan to-neon-green" },
    medium: { label: "30s", color: "from-neon-purple to-neon-pink" },
    long: { label: "60s", color: "from-neon-orange to-neon-pink" },
  };
  return map[type] || { label: type, color: "from-neon-purple to-neon-cyan" };
};

const VIDEO_PRELOAD = "metadata";

const safeVideoPlay = async (video: HTMLVideoElement | null) => {
  if (!video) return;
  try {
    await video.play();
  } catch {
    // Ignore autoplay/fullscreen play rejections.
  }
};

const VideoPlayerModal = ({ video, videos, onClose, onNavigate }: VideoPlayerModalProps) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  useGlobalVideoController(videoRef);
  const isMobile = useIsMobile();
  const [showSwipeHint, setShowSwipeHint] = useState(true);
  const [entering, setEntering] = useState(true);
  const [transitioning, setTransitioning] = useState(false);
  const [swipeOffset, setSwipeOffset] = useState(0);

  const touchStart = useRef<{ x: number; y: number } | null>(null);
  const touchDelta = useRef(0);

  const currentIndex = videos.findIndex((v) => v.id === video.id);
  const hasPrev = currentIndex > 0;
  const hasNext = currentIndex < videos.length - 1;

  const navigate = useCallback(
    (dir: "prev" | "next") => {
      const idx = dir === "prev" ? currentIndex - 1 : currentIndex + 1;
      if (idx < 0 || idx >= videos.length) return;
      setTransitioning(true);
      setTimeout(() => {
        onNavigate(videos[idx]);
        setTransitioning(false);
      }, 150);
      setShowSwipeHint(false);
    },
    [currentIndex, videos, onNavigate]
  );

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft" && hasPrev) navigate("prev");
      if (e.key === "ArrowRight" && hasNext) navigate("next");
    };
    document.addEventListener("keydown", handleKey);
    document.body.style.overflow = "hidden";
    requestAnimationFrame(() => setEntering(false));
    return () => {
      document.removeEventListener("keydown", handleKey);
      document.body.style.overflow = "";
    };
  }, [onClose, navigate, hasPrev, hasNext]);

  useEffect(() => {
    if (showSwipeHint) {
      const t = setTimeout(() => setShowSwipeHint(false), 3000);
      return () => clearTimeout(t);
    }
  }, [showSwipeHint]);

  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;

    // Set muted imperatively (not via React prop) so iOS fullscreen unmute sticks.
    el.muted = true;
    el.defaultMuted = true;
    el.setAttribute("muted", "");

    safeVideoPlay(el);

    const handleLoadedMetadata = () => {
      if (el.readyState >= 1) {
        el.currentTime = 0;
      }
    };

    el.addEventListener("loadedmetadata", handleLoadedMetadata);

    return () => {
      el.removeEventListener("loadedmetadata", handleLoadedMetadata);
      el.pause();
    };
  }, [video.id]);

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStart.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
    touchDelta.current = 0;
    setSwipeOffset(0);
  };
  const handleTouchMove = (e: React.TouchEvent) => {
    if (!touchStart.current) return;
    const dx = e.touches[0].clientX - touchStart.current.x;
    const dy = e.touches[0].clientY - touchStart.current.y;
    if (Math.abs(dx) > Math.abs(dy) * 1.2) {
      touchDelta.current = dx;
      // Show offset feedback capped at ±60px
      setSwipeOffset(Math.max(-60, Math.min(60, dx * 0.4)));
    }
  };
  const handleTouchEnd = () => {
    const delta = touchDelta.current;
    touchStart.current = null;
    setSwipeOffset(0);
    if (Math.abs(delta) > 50) {
      if (delta < 0 && hasNext) navigate("next");
      else if (delta > 0 && hasPrev) navigate("prev");
    }
  };

  const badge = formatBadge(video.format_type);
  const whatsappMsg = `Hi! I saw your "${video.title}" video and I'd like something similar for my business 🚀`;

  const stats = [
    { icon: Eye, value: formatNumber(video.views), label: "Views", color: "text-foreground" },
    { icon: Heart, value: formatNumber(video.likes), label: "Likes", color: "text-pink-400" },
    ...(video.comments ? [{ icon: MessageCircle, value: formatNumber(video.comments), label: "Comments", color: "text-neon-cyan" }] : []),
    ...(video.revenue > 0 ? [{ icon: TrendingUp, value: `€${formatNumber(video.revenue)}`, label: "Revenue", color: "text-neon-green" }] : []),
  ];

  // ── MOBILE ──
  if (isMobile) {
    return (
      <div className="fixed inset-0 z-[200] bg-black flex flex-col animate-fade-in">
        {/* Top bar */}
        <div className="flex items-center justify-between px-3 py-2 bg-black/80 backdrop-blur-sm z-30 flex-shrink-0">
          <span className="text-[11px] text-white/40 font-medium tabular-nums">
            {currentIndex + 1} / {videos.length}
          </span>
          <button onClick={onClose} className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white active:scale-90 transition-transform" aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Video with swipe */}
        <div
          className="flex-1 relative flex items-center justify-center overflow-hidden"
          style={{ transform: `translateX(${swipeOffset}px)`, transition: swipeOffset === 0 ? 'transform 0.2s ease-out' : 'none' }}
        >
          <video
            ref={videoRef}
            key={video.video_url}
            src={video.video_url}
            controls
            autoPlay
            playsInline
            preload={VIDEO_PRELOAD}
            poster={video.thumbnail_url || undefined}
            controlsList="nodownload noplaybackrate"
            className={cn("w-full h-full object-contain transition-opacity duration-150", transitioning ? "opacity-0" : "opacity-100")}
          />
          {/* Swipe zones on edges only so native video controls stay fully tappable */}
          <div
            className="absolute left-0 top-0 bottom-0 z-10 w-[18%]"
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          />
          <div
            className="absolute right-0 top-0 bottom-0 z-10 w-[18%]"
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          />
          {hasPrev && (
            <button onClick={() => navigate("prev")} className="absolute left-1 top-1/2 -translate-y-1/2 z-20 w-9 h-14 rounded-full bg-white/5 flex items-center justify-center text-white/40 active:bg-white/15 transition-colors">
              <ChevronLeft className="w-4 h-4" />
            </button>
          )}
          {hasNext && (
            <button onClick={() => navigate("next")} className="absolute right-1 top-1/2 -translate-y-1/2 z-20 w-9 h-14 rounded-full bg-white/5 flex items-center justify-center text-white/40 active:bg-white/15 transition-colors">
              <ChevronRight className="w-4 h-4" />
            </button>
          )}
          {showSwipeHint && videos.length > 1 && (
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 z-20 px-3 py-1.5 rounded-full bg-black/60 backdrop-blur-sm border border-white/10 animate-pulse">
              <span className="text-[10px] text-white/60">← Swipe to navigate →</span>
            </div>
          )}
        </div>

        {/* Bottom sheet with drag indicator */}
        <div className="flex-shrink-0 bg-gradient-to-t from-black via-black/95 to-black/80 border-t border-white/5 px-4 pt-2 pb-4">
          {/* Drag indicator */}
          <div className="flex justify-center mb-2">
            <div className="w-8 h-1 rounded-full bg-white/20" />
          </div>
          <div className="flex items-center gap-2 mb-1.5">
            {badge && (
              <div className={cn("px-2 py-0.5 rounded-full bg-gradient-to-r text-[9px] font-bold text-white uppercase tracking-wider flex items-center gap-1", badge.color)}>
                <Clock className="w-2.5 h-2.5" />{badge.label}
              </div>
            )}
            {video.campaign_type && (
              <div className="px-2 py-0.5 rounded-full bg-white/5 border border-white/10 text-[9px] text-white/60">{video.campaign_type}</div>
            )}
          </div>
          <h3 className="text-white font-heading font-bold text-sm leading-tight truncate">{video.title}</h3>
          <div className="flex items-center gap-1.5 text-white/40 text-[10px] mt-0.5">
            <MapPin className="w-2.5 h-2.5 flex-shrink-0" />
            <span className="truncate">{video.location}</span>
            <span className="mx-0.5">·</span>
            <span className="truncate">{video.industry}</span>
          </div>
          {/* Stats — scrollable */}
          <div className="flex gap-2 mt-2 overflow-x-auto scrollbar-hide -mx-1 px-1">
            {stats.map((stat) => (
              <div key={stat.label} className="flex items-center gap-1.5 bg-white/[0.06] border border-white/[0.04] rounded-lg px-2.5 py-1.5 flex-shrink-0">
                <stat.icon className={cn("w-3 h-3 flex-shrink-0", stat.color)} />
                <span className={cn("text-xs font-bold whitespace-nowrap tabular-nums", stat.color)}>{stat.value}</span>
                <span className="text-[8px] text-white/30 uppercase">{stat.label}</span>
              </div>
            ))}
          </div>
          {/* CTA */}
          <a
            href={getWhatsAppLink(whatsappMsg)}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-2 w-full mt-3 py-2.5 rounded-xl bg-gradient-to-r from-neon-green to-neon-cyan text-background text-xs font-bold active:scale-95 transition-transform shadow-[0_0_20px_hsl(var(--neon-green)/0.2)]"
          >
            <MessageCircle className="w-3.5 h-3.5" />
            Get One Like This — Free Sample
          </a>
        </div>
      </div>
    );
  }

  // ── DESKTOP: compact 2-column lightbox ──
  return (
    <div
      className={cn(
        "fixed inset-0 z-[200] flex items-center justify-center p-8 transition-all duration-300",
        entering ? "opacity-0" : "opacity-100"
      )}
      onClick={onClose}
    >
      <div className={cn("absolute inset-0 bg-black/80 backdrop-blur-xl transition-opacity duration-300", entering ? "opacity-0" : "opacity-100")} />

      {/* Nav arrows */}
      {hasPrev && (
        <button
          onClick={(e) => { e.stopPropagation(); navigate("prev"); }}
          className="absolute left-4 lg:left-8 top-1/2 -translate-y-1/2 z-30 w-11 h-11 rounded-full bg-white/[0.06] backdrop-blur-sm border border-white/[0.08] flex items-center justify-center text-white/50 hover:bg-white/[0.12] hover:text-white hover:scale-110 hover:border-white/20 transition-all duration-200"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
      )}
      {hasNext && (
        <button
          onClick={(e) => { e.stopPropagation(); navigate("next"); }}
          className="absolute right-4 lg:right-8 top-1/2 -translate-y-1/2 z-30 w-11 h-11 rounded-full bg-white/[0.06] backdrop-blur-sm border border-white/[0.08] flex items-center justify-center text-white/50 hover:bg-white/[0.12] hover:text-white hover:scale-110 hover:border-white/20 transition-all duration-200"
        >
          <ChevronRight className="w-5 h-5" />
        </button>
      )}

      {/* Modal shell */}
      <div
        className={cn(
          "relative z-20 flex gap-5 w-full transition-all duration-300 will-change-transform",
          "max-w-[720px] max-h-[min(calc(100vh-6rem),660px)]",
          entering ? "scale-95 opacity-0" : "scale-100 opacity-100"
        )}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Left: Video */}
        <div className="relative flex-shrink-0 w-[280px] lg:w-[310px] rounded-2xl overflow-hidden border border-white/[0.08] bg-black shadow-[0_0_80px_-20px_rgba(0,0,0,0.9)]">
          <div className="aspect-[9/16] relative">
            <video
              ref={videoRef}
              key={video.video_url}
              src={video.video_url}
              controls
              autoPlay
              playsInline
              preload={VIDEO_PRELOAD}
              poster={video.thumbnail_url || undefined}
              controlsList="nodownload noplaybackrate"
              className={cn("absolute inset-0 w-full h-full object-contain bg-black transition-opacity duration-150", transitioning ? "opacity-0" : "opacity-100")}
            />
            {badge && (
              <div className={cn("absolute top-3 left-3 z-10 px-2.5 py-1 rounded-full bg-gradient-to-r text-[10px] font-bold text-white uppercase tracking-wider flex items-center gap-1 pointer-events-none shadow-lg", badge.color)}>
                <Clock className="w-2.5 h-2.5" />{badge.label}
              </div>
            )}
          </div>
        </div>

        {/* Right: Info panel */}
        <div className="flex-1 min-w-0 flex flex-col overflow-hidden">
          {/* Close + counter */}
          <div className="flex justify-between items-center mb-3 flex-shrink-0">
            <span className="text-[11px] text-white/25 tabular-nums font-medium">{currentIndex + 1} of {videos.length}</span>
            <button
              onClick={onClose}
              className="w-9 h-9 rounded-full bg-white/[0.06] border border-white/[0.08] flex items-center justify-center text-white/50 hover:bg-white/[0.12] hover:text-white transition-all"
              aria-label="Close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Scrollable content */}
          <div className="flex-1 min-h-0 overflow-y-auto scrollbar-hide pr-1">
            {video.campaign_type && (
              <div className="inline-flex px-2.5 py-1 rounded-full bg-white/[0.04] border border-white/[0.06] text-[10px] text-white/50 font-medium mb-2">
                {video.campaign_type}
              </div>
            )}

            <h3 className="text-white font-heading font-bold text-lg lg:text-xl leading-tight mb-1">{video.title}</h3>
            <div className="flex items-center gap-2 text-white/40 text-xs mb-4">
              <MapPin className="w-3 h-3 flex-shrink-0" />
              <span>{video.location}</span>
              <span className="text-white/15">·</span>
              <span>{video.industry}</span>
            </div>

            {video.description && (
              <p className="text-white/30 text-xs leading-relaxed mb-4 line-clamp-2">{video.description}</p>
            )}

            {/* KPI Grid */}
            <div className="grid grid-cols-2 gap-2 mb-4">
              {stats.map((stat) => (
                <div key={stat.label} className="bg-white/[0.03] border border-white/[0.05] rounded-xl px-3 py-2.5 hover:bg-white/[0.05] transition-colors">
                  <div className="flex items-center gap-1.5 mb-0.5">
                    <stat.icon className={cn("w-3 h-3", stat.color)} />
                    <span className="text-[9px] text-white/30 uppercase tracking-wider font-medium">{stat.label}</span>
                  </div>
                  <div className={cn("text-base font-heading font-bold tabular-nums", stat.color)}>{stat.value}</div>
                </div>
              ))}
            </div>

            {/* ROI */}
            {video.roi_multiplier && video.roi_multiplier > 0 && (
              <div className="flex items-center gap-2 bg-neon-green/[0.06] border border-neon-green/15 rounded-xl px-3 py-2 mb-4">
                <TrendingUp className="w-4 h-4 text-neon-green" />
                <span className="text-sm font-bold text-neon-green">{video.roi_multiplier}× ROI</span>
                <span className="text-[10px] text-neon-green/50 ml-1">Return on investment</span>
              </div>
            )}
          </div>

          {/* Bottom CTA */}
          <div className="flex-shrink-0 pt-3 border-t border-white/[0.04]">
            <a
              href={getWhatsAppLink(whatsappMsg)}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 w-full py-2.5 rounded-xl bg-gradient-to-r from-neon-green to-neon-cyan text-background text-xs font-bold hover:shadow-[0_0_30px_hsl(var(--neon-green)/0.3)] hover:scale-[1.02] active:scale-[0.98] transition-all duration-200"
            >
              <Sparkles className="w-3.5 h-3.5" />
              Get One Like This — Free Sample
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VideoPlayerModal;
